/**
 * Levenshtein distance calculation for fuzzy matching
 * Returns the number of single-character edits (insertions, deletions, substitutions)
 */
export function levenshteinDistance(a: string, b: string): number {
  const aLower = a.toLowerCase();
  const bLower = b.toLowerCase();
  
  const matrix: number[][] = Array(bLower.length + 1)
    .fill(null)
    .map(() => Array(aLower.length + 1).fill(0));

  for (let i = 0; i <= aLower.length; i++) {
    matrix[0][i] = i;
  }

  for (let j = 0; j <= bLower.length; j++) {
    matrix[j][0] = j;
  }

  for (let j = 1; j <= bLower.length; j++) {
    for (let i = 1; i <= aLower.length; i++) {
      const indicator = aLower[i - 1] === bLower[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + indicator
      );
    }
  }

  return matrix[bLower.length][aLower.length];
}

/**
 * Check if a text contains a substring match (case-insensitive)
 */
export function containsSubstring(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.toLowerCase());
}

/**
 * Check if a text matches with tolerance for 1-character typos
 */
export function fuzzyMatch(text: string, query: string): boolean {
  if (containsSubstring(text, query)) {
    return true;
  }
  
  // Allow 1-character typo
  return levenshteinDistance(query, text) <= 1;
}

/**
 * Find all occurrences of query in text (case-insensitive)
 * Returns array of {start, length} for each match
 */
export interface TextMatch {
  start: number;
  length: number;
}

export function findMatches(text: string | null | undefined, query: string | null | undefined): TextMatch[] {
  if (!text || !query) {
    return [];
  }

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const matches: TextMatch[] = [];

  let startIndex = 0;
  while (true) {
    const index = lowerText.indexOf(lowerQuery, startIndex);
    if (index === -1) break;
    matches.push({ start: index, length: query.length });
    startIndex = index + 1;
  }

  return matches;
}

export type SearchScope = 'all' | 'deals' | 'contacts' | 'notes';

export interface ContactResult {
  type: 'contact';
  id: string;
  name: string;
  email: string;
  phone?: string;
  city?: string;
  isCompany?: boolean;
  ico?: string;
}

export interface DealResult {
  type: 'deal';
  id: string;
  title: string;
  contactId?: string;
  contactName?: string;
  contactEmail?: string;
  stageId?: string;
  stageName?: string;
  closingDate?: string;
  width?: string;
  height?: string;
  color?: string;
}

export interface NoteResult {
  type: 'note';
  id: string;
  body: string;
  dealId?: string;
  dealTitle?: string;
  contactId?: string;
  contactName?: string;
  contactEmail?: string;
  createdAt?: string;
}

export type SearchResult = ContactResult | DealResult | NoteResult;

/**
 * Score a search result based on match quality
 * Higher score = better match
 */
export function scoreResult(
  result: SearchResult,
  query: string
): number {
  const lowerQuery = query.toLowerCase();
  let score = 0;

  // Exact match in primary field gets highest score
  if (result.type === 'contact') {
    if (result.name.toLowerCase() === lowerQuery) score += 1000;
    else if (result.name.toLowerCase().includes(lowerQuery)) score += 500;
  } else if (result.type === 'deal') {
    if (result.title.toLowerCase() === lowerQuery) score += 1000;
    else if (result.title.toLowerCase().includes(lowerQuery)) score += 500;
  } else if (result.type === 'note') {
    if (result.body.toLowerCase().includes(lowerQuery)) score += 500;
  }

  // Penalize fuzzy matches (typos)
  const primaryField = result.type === 'contact' ? result.name : 
                       result.type === 'deal' ? result.title :
                       result.body;
  const distance = levenshteinDistance(query, primaryField);
  if (distance > 0) {
    score -= distance * 10;
  }

  // Bonus for secondary matches
  if (result.type === 'deal' && result.contactName?.toLowerCase().includes(lowerQuery)) {
    score += 100;
  } else if (result.type === 'note' && result.dealTitle?.toLowerCase().includes(lowerQuery)) {
    score += 100;
  }

  return Math.max(0, score);
}

/**
 * Format timestamp for display
 */
export function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return dateStr;
  }
}
